import React, { useEffect, useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import { Toaster, toast } from "react-hot-toast";
import { IStore } from "redux/store";
import toolActions from "redux/app/tool/actions";
import { Dispatch } from "redux";
import InfiniteProgress from "components/_common/InfiniteProgress";
import NumberedPagination from "components/_common/NumberedPagination";
import toolSelectors from "redux/app/tool/selectors";
import classNames from "classnames";
import commonUtils from "redux/_common/utils";

interface IPdfCronJob {
	jobType: string;
	jobRunId: string;
	status: string;
	totalJobs: number;
	processedJobs: number;
	successfulJobs: number;
	failedJobs: number;
	skippedJobs: number;
	estimatedTimeMinutes: number;
	startTime: string;
	endTime?: string;
	actualTimeMinutes?: number;
	progressPercentage: number;
	lastUpdated: string;
	dateRange: string;
	tenantCount: number;
	delayBetweenCalls: string;
	currentTask: string;
	remainingTasks: number;
	completedAt?: string;
	successRate?: string;
	estimatedCompletion?: {
		estimatedEndTime: string;
		remainingMinutes: number;
	} | null;
	isRunning: boolean;
	currentPhase?: string;
	currentTenant?: string;
	currentVessel?: string;
}

type PdfCronProps = PropsFromRedux;

function PdfCron(props: PdfCronProps) {
	const [isCurrentJobExpanded, setIsCurrentJobExpanded] = useState(false);
	const [loadingAction, setLoadingAction] = useState<string | null>(null);
	
	// Pagination state
	const [currentPage, setCurrentPage] = useState(1);
	const [itemsPerPage] = useState(10);

	useEffect(() => {
		props.loadPdfCronData(currentPage, itemsPerPage);
	}, [currentPage, itemsPerPage]); // eslint-disable-line react-hooks/exhaustive-deps

	// Handle page change
	const handlePageChange = (pageNumber: number, itemsPerPage: number) => {
		setCurrentPage(pageNumber);
		props.loadPdfCronData(pageNumber, itemsPerPage);
	};

	// Convert pagination data for NumberedPagination component
	const getPaginationForView = () => {
		const pagination = props.pdfCronPagination;
		const completedJobs = props.pdfCronData?.completedJobs || [];
		return {
			currentPage: pagination?.page || 1,
			itemsPerPage: pagination?.limit || 10,
			totalItems: pagination?.total || 0,
			itemIds: completedJobs.map((job: IPdfCronJob) => job.jobRunId),
		};
	};

	const formatDate = (dateString: string) => {
		return new Date(dateString).toLocaleString();
	};

	const formatDuration = (minutes: number | null | undefined) => {
		if (minutes === null || minutes === undefined) return "0";
		const hours = Math.floor(minutes / 60);
		const mins = minutes % 60;
		return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
	};

	const getPhaseMessage = (job: IPdfCronJob) => {
		if (job.status?.toLowerCase() !== 'gathering_data') {
			return null;
		}

		if (job.currentPhase === 'fetching_logs' && job.currentTenant && job.currentVessel) {
			return `Fetching Logs for ${job.currentTenant} - ${job.currentVessel}`;
		}

		return 'Gathering data...';
	};

	const getStatusBadge = (status: string) => {
		const baseClasses = "px-3 py-1 rounded-full text-xs font-medium";
		switch (status.toLowerCase()) {
			case "running":
				return `${baseClasses} bg-blue-100 text-blue-800`;
			case "processing":
				return `${baseClasses} bg-purple-100 text-purple-800 animate-pulse`;
			case "gathering_data":
				return `${baseClasses} bg-yellow-100 text-yellow-800 animate-pulse`;
			case "completed":
				return `${baseClasses} bg-green-100 text-green-800`;
			case "failed":
			case "stopped":
				return `${baseClasses} bg-red-100 text-red-800`;
			default:
				return `${baseClasses} bg-gray-100 text-gray-800`;
		}
	};

	const handleServiceControl = async (action: "start" | "stop" | "restart") => {
		setLoadingAction(action);
		try {
			const baseUrl = commonUtils.getLogsPdfBaseUrl();
			const response = await fetch(`${baseUrl}/${action}/pdf-cron`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
			});

			if (response.ok) {
				const data = await response.json();
				toast.success(data.message || `Service ${action}ed successfully`);
				// Reload data after a short delay
				setTimeout(() => {
					props.loadPdfCronData(currentPage, itemsPerPage);
				}, 1000);
			} else {
				toast.error(`Failed to ${action} service`);
			}
		} catch (error) {
			console.error(`Error ${action}ing service:`, error);
			toast.error(`Error ${action}ing service`);
		} finally {
			setLoadingAction(null);
		}
	};

	const renderCurrentJob = (job: IPdfCronJob) => {
		return (
			<div className="bg-white border border-gray-200 rounded-lg mb-6">
				{/* Accordion Header */}
				<div className="flex items-center justify-between p-6 hover:bg-gray-50 transition-colors">
					<div className="flex items-center gap-3">
						<h3 className="text-lg font-semibold text-gray-900">Active Job</h3>
						<span className={getStatusBadge(job.status)}>{job.status}</span>
						{getPhaseMessage(job) && (
							<span className="text-sm text-gray-600 italic">
								{getPhaseMessage(job)}
							</span>
						)}
					</div>
					<div className="flex items-center gap-4">
						<div className="text-gray-500 text-sm">
							{job.status?.toLowerCase() === 'processing' ? `${job.progressPercentage}%` :  job.status?.toLowerCase() === 'gathering_data' ? (
								<div className="w-8 h-4 bg-gray-200 rounded animate-pulse"></div>
							) : (
								<div className="w-8 h-4">0</div>
							)}
						</div>
						{/* Progress Bar */}
						<div className="w-64">
							<div className="w-full bg-gray-200 rounded-full h-2">
								{job.status?.toLowerCase() === 'processing' ? (
									<div
										className="bg-blue-600 h-2 rounded-full transition-all duration-300"
										style={{ width: `${job.progressPercentage}%` }}
									></div>
								) : job.status?.toLowerCase() === 'gathering_data' ? (
									<div className="bg-gray-300 h-2 rounded-full w-1/4 animate-pulse"></div>
								) : (
									<div className="bg-gray-300 h-2 rounded-full" style={{ width: '0%' }}></div>
								)}
							</div>
						</div>
						
						<div
							className="cursor-pointer"
							onClick={() => setIsCurrentJobExpanded(!isCurrentJobExpanded)}
						>
							<svg
								className={`w-5 h-5 text-gray-400 transition-transform ${
									isCurrentJobExpanded ? "rotate-180" : ""
								}`}
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M19 9l-7 7-7-7"
								/>
							</svg>
						</div>
					</div>
				</div>
				{/* Accordion Content */}
				{isCurrentJobExpanded && (
					<div className="px-6 pb-6 border-t border-gray-100">
						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4 pt-4">
							{/* Progress */}
							<div className="bg-gray-50 p-3 rounded">
								<div className="text-sm text-gray-500">Progress</div>
								<div className="font-medium text-gray-900">
									{job.status?.toLowerCase() === 'processing' ? (
										`${job.progressPercentage}%`
									) : job.status?.toLowerCase() === 'gathering_data' ? (
										<div className="w-12 h-5 bg-gray-200 rounded animate-pulse"></div>
									) : (
										<div className="w-12 h-5">0%</div>
									)}
								</div>
							</div>
							
							{/* Total Jobs */}
							<div className="bg-gray-50 p-3 rounded">
								<div className="text-sm text-gray-500">Total Jobs</div>
								<div className="font-medium text-gray-900">
									{job.status?.toLowerCase() === 'processing' ? (
										job.totalJobs
									) : job.status?.toLowerCase() === 'gathering_data' ? (
										<div className="w-16 h-5 bg-gray-200 rounded animate-pulse"></div>
									) : (
										<div className="w-16 h-5">0</div>
									)}
								</div>
							</div>
							
							{/* Processed */}
							<div className="bg-gray-50 p-3 rounded">
								<div className="text-sm text-gray-500">Processed</div>
								<div className="font-medium text-gray-900">
									{job.status?.toLowerCase() === 'processing' ? (
										job.processedJobs
									) : job.status?.toLowerCase() === 'gathering_data' ? (
										<div className="w-16 h-5 bg-gray-200 rounded animate-pulse"></div>
									) : (
										<div className="w-16 h-5">0</div>
									)}
								</div>
							</div>
							
							{/* Successful */}
							<div className="bg-gray-50 p-3 rounded">
								<div className="text-sm text-gray-500">Successful</div>
								<div className="font-medium text-green-600">
									{job.status?.toLowerCase() === 'processing' ? (
										job.successfulJobs
									) : job.status?.toLowerCase() === 'gathering_data' ? (
										<div className="w-16 h-5 bg-gray-200 rounded animate-pulse"></div>
									) : (
										<div className="w-16 h-5">0</div>
									)}
								</div>
							</div>
							
						{/* Failed */}
						<div className="bg-gray-50 p-3 rounded">
							<div className="text-sm text-gray-500">Failed</div>
							<div className="font-medium text-red-600">
								{job.status?.toLowerCase() === 'processing' ? (
									job.failedJobs
								) : job.status?.toLowerCase() === 'gathering_data' ? (
									<div className="w-16 h-5 bg-gray-200 rounded animate-pulse"></div>
								) : (
									<div className="w-16 h-5">0</div>
								)}
							</div>
						</div>
						
						{/* Skipped */}
						<div className="bg-gray-50 p-3 rounded">
							<div className="text-sm text-gray-500">Skipped</div>
							<div className="font-medium text-yellow-600">
								{job.status?.toLowerCase() === 'processing' ? (
									job.skippedJobs
								) : job.status?.toLowerCase() === 'gathering_data' ? (
									<div className="w-16 h-5 bg-gray-200 rounded animate-pulse"></div>
								) : (
									<div className="w-16 h-5">0</div>
								)}
							</div>
						</div>
						
						{/* Date Range */}
							<div className="bg-gray-50 p-3 rounded">
								<div className="text-sm text-gray-500">Date Range</div>
								<div className="font-medium text-gray-900">
									{job.status?.toLowerCase() === 'processing' ? (
										job.dateRange
									) : job.status?.toLowerCase() === 'gathering_data' ? (
										<div className="w-24 h-5 bg-gray-200 rounded animate-pulse"></div>
									) : (
										<div className="w-24 h-5">-</div>
									)}
								</div>
							</div>
							
							{/* Start Time */}
							<div className="bg-gray-50 p-3 rounded">
								<div className="text-sm text-gray-500">Start Time</div>
								<div className="font-medium text-gray-900">
									{job.status?.toLowerCase() === 'processing' ? (
										formatDate(job.startTime)
									) : job.status?.toLowerCase() === 'gathering_data' ? (
										<div className="w-32 h-5 bg-gray-200 rounded animate-pulse"></div>
									) : (
										<div className="w-32 h-5">-</div>
									)}
								</div>
							</div>
							
							{/* Estimated Time */}
							<div className="bg-gray-50 p-3 rounded">
								<div className="text-sm text-gray-500">Estimated Time</div>
								<div className="font-medium text-gray-900">
									{job.status?.toLowerCase() === 'processing' ? (
										formatDuration(job.estimatedTimeMinutes)
									) : job.status?.toLowerCase() === 'gathering_data' ? (
										<div className="w-20 h-5 bg-gray-200 rounded animate-pulse"></div>
									) : (
										<div className="w-20 h-5">0</div>
									)}
								</div>
							</div>
							
							{/* Remaining Tasks */}
							<div className="bg-gray-50 p-3 rounded">
								<div className="text-sm text-gray-500">Remaining Tasks</div>
								<div className="font-medium text-gray-900">
									{job.status?.toLowerCase() === 'processing' ? (
										job.remainingTasks
									) : job.status?.toLowerCase() === 'gathering_data' ? (
										<div className="w-16 h-5 bg-gray-200 rounded animate-pulse"></div>
									) : (
										<div className="w-16 h-5">0</div>
									)}
								</div>
							</div>
							
							{/* Estimated End Time */}
							{job.status?.toLowerCase() === 'processing' && job.estimatedCompletion && (
								<div className="bg-gray-50 p-3 rounded">
									<div className="text-sm text-gray-500">
										Estimated End Time
									</div>
									<div className="font-medium text-gray-900">
										{formatDate(job.estimatedCompletion.estimatedEndTime)}
									</div>
								</div>
							)}
							
							{/* Estimated End Time Skeleton */}
							{job.status?.toLowerCase() === 'gathering_data' && (
								<div className="bg-gray-50 p-3 rounded">
									<div className="text-sm text-gray-500">
										Estimated End Time
									</div>
									<div className="font-medium text-gray-900">
										<div className="w-32 h-5 bg-gray-200 rounded animate-pulse"></div>
									</div>
								</div>
							)}
							
							{/* Estimated End Time Default */}
							{job.status?.toLowerCase() !== 'processing' && job.status?.toLowerCase() !== 'gathering_data' && (
								<div className="bg-gray-50 p-3 rounded">
									<div className="text-sm text-gray-500">
										Estimated End Time
									</div>
									<div className="font-medium text-gray-900">
										<div className="w-32 h-5">-</div>
									</div>
								</div>
							)}
						</div>
					</div>
				)}
			</div>
		);
	};

	const renderCompletedJobs = (jobs: IPdfCronJob[]) => {
		return (
			<div className="bg-white rounded-lg">
				<div className="px-6 py-4 border-b border-gray-200">
					<h3 className="text-lg font-semibold text-gray-900">
						Completed Jobs ({jobs.length})
					</h3>
				</div>
				<div className="overflow-x-auto">
					<InfiniteProgress
						isLoading={props.jobToLoadPdfCronStats?.isLoading}
						isSpacedOut={false}
					/>
					<table
						className={classNames(
							"min-w-full divide-y border border-gray-200 divide-gray-200",
							{ "animate-pulse": props.jobToLoadPdfCronStats?.isLoading }
						)}
					>
						<thead className="bg-gray-50">
							<tr>
								<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
									Status
								</th>
								<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
									Total Jobs
								</th>
								<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
									Processed
								</th>
								<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
									Successful
								</th>
								<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
									Failed
								</th>
								<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
									Skipped
								</th>
								<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
									Duration
								</th>
								<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
									Start Time
								</th>
								<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
									End Time
								</th>
								<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
									Success Rate
								</th>
							</tr>
						</thead>
						<tbody
							className={classNames("bg-white divide-y divide-gray-200", {
								"h-0 opacity-0 transition-all duration-100 max-h-0":
									props.jobToLoadPdfCronStats?.isLoading,
								"h-auto opacity-100 transition-all duration-1000 delay-300 max-h-auto":
									!props.jobToLoadPdfCronStats?.isLoading,
							})}
						>
							{!props.jobToLoadPdfCronStats?.isLoading && (
								<>
									{jobs.length === 0 ? (
										<tr>
											<td colSpan={10} className="text-center py-12">
												<div className="flex flex-col items-center justify-center text-gray-500">
													<svg
														className="w-16 h-16 mb-4 text-gray-300"
														fill="none"
														stroke="currentColor"
														viewBox="0 0 24 24"
													>
														<path
															strokeLinecap="round"
															strokeLinejoin="round"
															strokeWidth={1.5}
															d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
														/>
													</svg>
													<h3 className="text-xl font-medium text-gray-900 mb-2">
														No Completed Jobs Found
													</h3>
													<p className="text-sm text-gray-500 max-w-md text-center">
														No completed PDF generation jobs were found. Jobs
														will appear here once they are completed.
													</p>
												</div>
											</td>
										</tr>
									) : (
										jobs.map((job) => (
											<tr key={job.jobRunId} className="hover:bg-gray-50">
												<td className="px-6 py-4 whitespace-nowrap">
													<span className={getStatusBadge(job.status)}>
														{job.status}
													</span>
												</td>
												<td className="px-6 py-4 whitespace-nowrap">
													<div className="text-sm font-medium text-gray-900">
														{job.totalJobs}
													</div>
												</td>
												<td className="px-6 py-4 whitespace-nowrap">
													<div className="text-sm font-medium text-gray-900">
														{job.processedJobs}
													</div>
												</td>
												<td className="px-6 py-4 whitespace-nowrap">
													<div className="text-sm font-medium text-green-600">
														{job.successfulJobs}
													</div>
												</td>
												<td className="px-6 py-4 whitespace-nowrap">
													<div className="text-sm font-medium text-red-600">
														{job.failedJobs}
													</div>
												</td>
												<td className="px-6 py-4 whitespace-nowrap">
													<div className="text-sm font-medium text-yellow-600">
														{job.skippedJobs}
													</div>
												</td>
												<td className="px-6 py-4 whitespace-nowrap">
													<div className="text-sm font-medium text-gray-900">
														{formatDuration(job.actualTimeMinutes)}
													</div>
												</td>
												<td className="px-6 py-4 whitespace-nowrap">
													<div className="text-sm font-medium text-gray-900">
														{formatDate(job.startTime)}
													</div>
												</td>
												<td className="px-6 py-4 whitespace-nowrap">
													<div className="text-sm font-medium text-gray-900">
														{job.endTime ? formatDate(job.endTime) : "N/A"}
													</div>
												</td>
												<td className="px-6 py-4 whitespace-nowrap">
													<div className="text-sm font-medium text-gray-900">
														{job.successRate + "%" || "0"}
													</div>
												</td>
											</tr>
										))
									)}
								</>
							)}
						</tbody>
					</table>
				</div>
				{/* Pagination */}
				{getPaginationForView().totalItems > 0 && (
					<div className="px-6 py-4 border-t border-gray-200">
						<NumberedPagination
							currentPage={getPaginationForView().currentPage}
							itemsPerPage={getPaginationForView().itemsPerPage}
							totalItems={getPaginationForView().totalItems}
							itemIds={getPaginationForView().itemIds}
							onPageChange={handlePageChange}
							isPageLoading={props.jobToLoadPdfCronStats?.isLoading}
						/>
					</div>
				)}
			</div>
		);
	};

	return (
		<div className="flex flex-col gap-6 w-full h-full">
			<Toaster
				position="top-right"
				toastOptions={{
					duration: 3000,
					style: {
						background: "#333",
						color: "#fff",
					},
				}}
			/>

			{props.jobToLoadPdfCronStats?.isError && (
				<div className="bg-red-50 border border-red-200 rounded-md p-4">
					<div className="text-sm text-red-700">
						{props.jobToLoadPdfCronStats.message ||
							"Failed to load PDF cron data"}
					</div>
				</div>
			)}

			{!props.jobToLoadPdfCronStats?.isLoading && props.pdfCronData ? (
				<>
					{/* Statistics Cards */}
					<div className="flex gap-4">
						<div className="bg-white border flex-1 border-gray-200 rounded-lg p-4">
							<div className="text-sm text-gray-500">Total Jobs in History</div>
							<div className="text-2xl font-semibold text-gray-900">
								{props.pdfCronData.statistics.totalJobsInHistory}
							</div>
						</div>
						<div className="bg-white border flex-1 border-gray-200 rounded-lg p-4">
							<div className="text-sm text-gray-500">Completed Jobs</div>
							<div className="text-2xl font-semibold text-green-600">
								{props.pdfCronData.statistics.completedJobsCount}
							</div>
						</div>
						<div className="bg-white border flex-1 border-gray-200 rounded-lg p-4">
							<div className="text-sm text-gray-500">Stopped Jobs</div>
							<div className="text-2xl font-semibold text-red-600">
								{props.pdfCronData.statistics.stoppedJobsCount}
							</div>
						</div>
						<div className="bg-white border flex-1 border-gray-200 rounded-lg p-4">
							<div className="text-sm text-gray-500">Running Jobs</div>
							<div className="text-2xl font-semibold text-blue-600">
								{props.pdfCronData.statistics.runningJobsCount}
							</div>
						</div>
					</div>

					{/* Current Job */}
					{props.jobToLoadPdfCronStats?.isLoading ? (
						<div className="bg-white border border-gray-200 rounded-lg mb-6">
							<div className="p-6">
								<div className="flex items-center justify-between">
									<div className="flex items-center gap-3">
										<div className="w-20 h-6 bg-gray-200 rounded animate-pulse"></div>
										<div className="w-16 h-5 bg-gray-200 rounded animate-pulse"></div>
									</div>
									<div className="flex items-center gap-4">
										<div className="w-32 h-2 bg-gray-200 rounded-full animate-pulse"></div>
										<div className="w-5 h-5 bg-gray-200 rounded animate-pulse"></div>
									</div>
								</div>
							</div>
						</div>
					) : props.pdfCronData.currentJob ? (
						renderCurrentJob(props.pdfCronData.currentJob)
					) : (
						<div className="bg-white border border-gray-200 rounded-lg mb-6">
							<div className="flex items-center justify-between p-6 hover:bg-gray-50 transition-colors">
								<h3 className="text-base font-medium text-gray-900">
									No Active Jobs Found
								</h3>
							</div>
						</div>
					)}

					{/* Completed Jobs Table */}
					{renderCompletedJobs(props.pdfCronData.completedJobs)}
				</>
			) : (
				<>
					{/* Loading Statistics Cards */}
					<div className="flex gap-4">
						<div className="bg-white border flex-1 border-gray-200 rounded-lg p-4">
							<div className="text-sm text-gray-500">Total Jobs in History</div>
							<div className="text-2xl font-semibold text-gray-900">
								{props.jobToLoadPdfCronStats?.isLoading ? (
									<div className="w-8 h-6 bg-gray-200 rounded animate-pulse"></div>
								) : props.pdfCronData?.statistics ? (
									props.pdfCronData.statistics.totalJobsInHistory
								) : (
									"N/A"
								)}
							</div>
						</div>
						<div className="bg-white border flex-1 border-gray-200 rounded-lg p-4">
							<div className="text-sm text-gray-500">Completed Jobs</div>
							<div className="text-2xl font-semibold text-green-600">
								{props.jobToLoadPdfCronStats?.isLoading ? (
									<div className="w-8 h-6 bg-gray-200 rounded animate-pulse"></div>
								) : props.pdfCronData?.statistics ? (
									props.pdfCronData.statistics.completedJobsCount
								) : (
									"N/A"
								)}
							</div>
						</div>
						<div className="bg-white border flex-1 border-gray-200 rounded-lg p-4">
							<div className="text-sm text-gray-500">Stopped Jobs</div>
							<div className="text-2xl font-semibold text-blue-600">
								{props.jobToLoadPdfCronStats?.isLoading ? (
									<div className="w-8 h-6 bg-gray-200 rounded animate-pulse"></div>
								) : props.pdfCronData?.statistics ? (
									props.pdfCronData.statistics.stoppedJobsCount
								) : (
									"N/A"
								)}
							</div>
						</div>
						<div className="bg-white border flex-1 border-gray-200 rounded-lg p-4">
							<div className="text-sm text-gray-500">Running Jobs</div>
							<div className="text-2xl font-semibold text-blue-600">
								{props.jobToLoadPdfCronStats?.isLoading ? (
									<div className="w-8 h-6 bg-gray-200 rounded animate-pulse"></div>
								) : props.pdfCronData?.statistics ? (
									props.pdfCronData.statistics.runningJobsCount
								) : (
									"N/A"
								)}
							</div>
						</div>
					</div>

					<div className="bg-white border border-gray-200 rounded-lg mb-6">
						<div className="p-6">
							<div className="flex items-center justify-between">
								<div className="flex items-center gap-3">
									<div className="w-20 h-6 bg-gray-200 rounded animate-pulse"></div>
									<div className="w-16 h-5 bg-gray-200 rounded animate-pulse"></div>
								</div>
								<div className="flex items-center gap-4">
									<div className="w-32 h-2 bg-gray-200 rounded-full animate-pulse"></div>
									<div className="w-5 h-5 bg-gray-200 rounded animate-pulse"></div>
								</div>
							</div>
						</div>
					</div>

					{/* Loading Table */}
					{renderCompletedJobs([])}
				</>
			)}
		</div>
	);
}

function mapStateToProps(store: IStore) {
	const toolStore = (store as any).app.tool;
	return {
		pdfCronData: toolStore?.pdfCronData,
		pdfCronPagination: toolSelectors.getPdfCronPagination(toolStore),
		jobToLoadPdfCronStats: toolSelectors.jobToLoadPdfCronStats(toolStore),
	};
}

function mapDispatchToProps(dispatch: Dispatch) {
	return {
		loadPdfCronData: (page: number = 1, limit: number = 10) => 
			dispatch(toolActions.commands.toolPdfCronLoad(page, limit)),
	};
}

const reduxConnector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof reduxConnector>;
export default reduxConnector(PdfCron);
